/**
 * Dynamic Configuration Section
 * PROJ-045 - Node Properties Panel Enhancement
 *
 * Renders product-specific configuration forms based on JSON Schema.
 * Supports various field types: string, number, boolean, enum, password, etc.
 */

import { useState, useCallback, useMemo, type ReactNode } from 'react';
import { Settings, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { CollapsibleSection } from '@/components/ui/collapsible-section';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Schema types
export interface PropertySchema {
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  title: string;
  description?: string;
  enum?: string[];
  default?: unknown;
  format?: 'password' | 'uri' | 'email' | 'date' | 'textarea';
  required?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
}

export interface ConfigGroup {
  title: string;
  icon?: string;
  fields: string[];
  collapsed?: boolean;
}

export interface ConfigSchema {
  type: 'object';
  properties: Record<string, PropertySchema>;
  required?: string[];
  groups?: ConfigGroup[];
}

interface DynamicConfigSectionProps {
  nodeId: string;
  schema: ConfigSchema;
  config?: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
  productName?: string;
}

export function DynamicConfigSection({
  nodeId,
  schema,
  config = {},
  onChange,
  productName
}: DynamicConfigSectionProps) {
  // Track which password fields are visible
  const [visiblePasswords, setVisiblePasswords] = useState<Set<string>>(new Set());

  // Toggle password visibility
  const togglePasswordVisibility = useCallback((fieldName: string) => {
    setVisiblePasswords((prev) => {
      const next = new Set(prev);
      if (next.has(fieldName)) {
        next.delete(fieldName);
      } else {
        next.add(fieldName);
      }
      return next;
    });
  }, []);

  // Handle field value change
  const handleFieldChange = useCallback((fieldName: string, value: unknown) => {
    onChange({
      ...config,
      [fieldName]: value
    });
  }, [config, onChange]);

  // Get value with default fallback
  const getFieldValue = useCallback((fieldName: string, propSchema: PropertySchema): unknown => {
    if (config[fieldName] !== undefined) {
      return config[fieldName];
    }
    if (propSchema.default !== undefined) {
      return propSchema.default;
    }
    // Type-specific defaults
    switch (propSchema.type) {
      case 'boolean':
        return false;
      case 'number':
        return propSchema.min ?? 0;
      case 'string':
        return '';
      default:
        return '';
    }
  }, [config]);

  // Check if field is required
  const isRequired = useCallback((fieldName: string, propSchema: PropertySchema): boolean => {
    return propSchema.required === true || (schema.required?.includes(fieldName) ?? false);
  }, [schema.required]);

  // Render a single field based on its schema
  const renderField = useCallback((fieldName: string, propSchema: PropertySchema) => {
    const value = getFieldValue(fieldName, propSchema);
    const required = isRequired(fieldName, propSchema);

    // String with enum -> Select
    if (propSchema.type === 'string' && propSchema.enum) {
      return (
        <div key={fieldName} className="space-y-1.5">
          <Label className="text-xs font-medium flex items-center gap-1">
            {propSchema.title}
            {required && <span className="text-destructive">*</span>}
          </Label>
          <Select
            value={String(value || '')}
            onValueChange={(v) => handleFieldChange(fieldName, v)}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder={propSchema.placeholder || `Select ${propSchema.title}...`} />
            </SelectTrigger>
            <SelectContent>
              {propSchema.enum.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {propSchema.description && (
            <p className="text-xs text-muted-foreground">{propSchema.description}</p>
          )}
        </div>
      );
    }

    // Boolean -> Switch
    if (propSchema.type === 'boolean') {
      return (
        <div key={fieldName} className="flex items-center justify-between py-1">
          <div className="space-y-0.5">
            <Label className="text-xs font-medium">{propSchema.title}</Label>
            {propSchema.description && (
              <p className="text-xs text-muted-foreground">{propSchema.description}</p>
            )}
          </div>
          <Switch
            checked={Boolean(value)}
            onCheckedChange={(checked) => handleFieldChange(fieldName, checked)}
          />
        </div>
      );
    }

    // Number -> Number input
    if (propSchema.type === 'number') {
      return (
        <div key={fieldName} className="space-y-1.5">
          <Label className="text-xs font-medium flex items-center gap-1">
            {propSchema.title}
            {required && <span className="text-destructive">*</span>}
          </Label>
          <Input
            type="number"
            value={String(value ?? '')}
            onChange={(e) => handleFieldChange(fieldName, e.target.value ? Number(e.target.value) : undefined)}
            placeholder={propSchema.placeholder}
            min={propSchema.min}
            max={propSchema.max}
            className="h-8 text-sm"
          />
          {propSchema.description && (
            <p className="text-xs text-muted-foreground">{propSchema.description}</p>
          )}
        </div>
      );
    }

    // String with format=password -> Password input with toggle
    if (propSchema.type === 'string' && propSchema.format === 'password') {
      const isVisible = visiblePasswords.has(fieldName);
      return (
        <div key={fieldName} className="space-y-1.5">
          <Label className="text-xs font-medium flex items-center gap-1">
            {propSchema.title}
            {required && <span className="text-destructive">*</span>}
          </Label>
          <div className="relative">
            <Input
              type={isVisible ? 'text' : 'password'}
              value={String(value || '')}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
              placeholder={propSchema.placeholder || '••••••••'}
              className="h-8 text-sm pr-10"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-8 w-8"
              onClick={() => togglePasswordVisibility(fieldName)}
            >
              {isVisible ? (
                <EyeOff className="h-3.5 w-3.5" />
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
          {propSchema.description && (
            <p className="text-xs text-muted-foreground">{propSchema.description}</p>
          )}
        </div>
      );
    }

    // String with format=textarea -> Textarea
    if (propSchema.type === 'string' && propSchema.format === 'textarea') {
      return (
        <div key={fieldName} className="space-y-1.5">
          <Label className="text-xs font-medium flex items-center gap-1">
            {propSchema.title}
            {required && <span className="text-destructive">*</span>}
          </Label>
          <Textarea
            value={String(value || '')}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            placeholder={propSchema.placeholder}
            className="text-sm min-h-[60px] resize-none"
            rows={3}
          />
          {propSchema.description && (
            <p className="text-xs text-muted-foreground">{propSchema.description}</p>
          )}
        </div>
      );
    }

    // String with format=uri -> URL input
    if (propSchema.type === 'string' && propSchema.format === 'uri') {
      return (
        <div key={fieldName} className="space-y-1.5">
          <Label className="text-xs font-medium flex items-center gap-1">
            {propSchema.title}
            {required && <span className="text-destructive">*</span>}
          </Label>
          <Input
            type="url"
            value={String(value || '')}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            placeholder={propSchema.placeholder || 'https://...'}
            className="h-8 text-sm"
          />
          {propSchema.description && (
            <p className="text-xs text-muted-foreground">{propSchema.description}</p>
          )}
        </div>
      );
    }

    // String with format=email -> Email input
    if (propSchema.type === 'string' && propSchema.format === 'email') {
      return (
        <div key={fieldName} className="space-y-1.5">
          <Label className="text-xs font-medium flex items-center gap-1">
            {propSchema.title}
            {required && <span className="text-destructive">*</span>}
          </Label>
          <Input
            type="email"
            value={String(value || '')}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            placeholder={propSchema.placeholder || 'email@example.com'}
            className="h-8 text-sm"
          />
          {propSchema.description && (
            <p className="text-xs text-muted-foreground">{propSchema.description}</p>
          )}
        </div>
      );
    }

    // String with format=date -> Date input
    if (propSchema.type === 'string' && propSchema.format === 'date') {
      return (
        <div key={fieldName} className="space-y-1.5">
          <Label className="text-xs font-medium flex items-center gap-1">
            {propSchema.title}
            {required && <span className="text-destructive">*</span>}
          </Label>
          <Input
            type="date"
            value={String(value || '')}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className="h-8 text-sm"
          />
          {propSchema.description && (
            <p className="text-xs text-muted-foreground">{propSchema.description}</p>
          )}
        </div>
      );
    }

    // Default: String -> Text input
    return (
      <div key={fieldName} className="space-y-1.5">
        <Label className="text-xs font-medium flex items-center gap-1">
          {propSchema.title}
          {required && <span className="text-destructive">*</span>}
        </Label>
        <Input
          type="text"
          value={String(value || '')}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
          placeholder={propSchema.placeholder}
          className="h-8 text-sm"
        />
        {propSchema.description && (
          <p className="text-xs text-muted-foreground">{propSchema.description}</p>
        )}
      </div>
    );
  }, [getFieldValue, isRequired, handleFieldChange, visiblePasswords, togglePasswordVisibility]);

  // Organize fields by groups
  const { groupedFields, ungroupedFields } = useMemo(() => {
    const allFieldNames = Object.keys(schema.properties);
    const grouped: { group: ConfigGroup; fields: string[] }[] = [];
    const ungrouped: string[] = [];

    if (schema.groups && schema.groups.length > 0) {
      const assignedFields = new Set<string>();

      for (const group of schema.groups) {
        const groupFields = group.fields.filter((f) => allFieldNames.includes(f));
        if (groupFields.length > 0) {
          grouped.push({ group, fields: groupFields });
          groupFields.forEach((f) => assignedFields.add(f));
        }
      }

      // Fields not in any group
      for (const fieldName of allFieldNames) {
        if (!assignedFields.has(fieldName)) {
          ungrouped.push(fieldName);
        }
      }
    } else {
      // No groups defined, all fields are ungrouped
      ungrouped.push(...allFieldNames);
    }

    return { groupedFields: grouped, ungroupedFields: ungrouped };
  }, [schema]);

  // Count configured fields for badge
  const configuredCount = useMemo(() => {
    return Object.keys(config).filter((k) => config[k] !== undefined && config[k] !== '').length;
  }, [config]);

  const totalFields = Object.keys(schema.properties).length;

  return (
    <CollapsibleSection
      id={`${nodeId}-config`}
      title={productName ? `${productName} Configuration` : 'Product Configuration'}
      icon={Settings}
      defaultOpen={false}
      badge={
        configuredCount > 0 ? (
          <Badge variant="secondary" className="ml-2 text-xs">
            {configuredCount}/{totalFields}
          </Badge>
        ) : null
      }
    >
      <div className="space-y-4">
        {/* Render grouped fields */}
        {groupedFields.map(({ group, fields }) => (
          <FieldGroup
            key={group.title}
            title={group.title}
            icon={group.icon}
            defaultCollapsed={group.collapsed}
          >
            <div className="space-y-3">
              {fields.map((fieldName) => {
                const propSchema = schema.properties[fieldName];
                if (!propSchema) return null;
                return renderField(fieldName, propSchema);
              })}
            </div>
          </FieldGroup>
        ))}

        {/* Render ungrouped fields */}
        {ungroupedFields.length > 0 && (
          <div className="space-y-3">
            {ungroupedFields.map((fieldName) => {
              const propSchema = schema.properties[fieldName];
              if (!propSchema) return null;
              return renderField(fieldName, propSchema);
            })}
          </div>
        )}

        {/* Empty state */}
        {totalFields === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">
            No configuration options available
          </p>
        )}
      </div>
    </CollapsibleSection>
  );
}

// Sub-component for field groups
interface FieldGroupProps {
  title: string;
  icon?: string;
  defaultCollapsed?: boolean;
  children: ReactNode;
}

function FieldGroup({ title, icon, defaultCollapsed = false, children }: FieldGroupProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  return (
    <div className="border rounded-md">
      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between p-2 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon && <span className="text-sm">{icon}</span>}
          <span className="text-sm font-medium">{title}</span>
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform duration-200",
            !isCollapsed && "rotate-180"
          )}
        />
      </button>
      <div
        className={cn(
          "overflow-hidden transition-all duration-200",
          !isCollapsed ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="p-3 pt-0 border-t">
          {children}
        </div>
      </div>
    </div>
  );
}
